import { Navigate, Route, Routes } from 'react-router-dom'
import { User } from 'firebase/auth'

import Home from '../pages/Home'
import Landing from '../pages/Landing'
import NotFound from '../pages/NotFound'
import VerifyEmail from '../pages/VerifyEmail'
import { ProfileCompletionGuard, EmailVerificationGuard } from './guards'
import { renderRoutes } from './utils/routeHelpers'

// Import route definitions
import userRoutes from './UserRoutes'
import adminRoutes from './AdminRoutes'
import guestRoutes from './GuestRoutes'
import advancedRoutes from './AdvancedRoutes'

interface AppRoutesProps {
  user: User | null
  isAdmin: boolean
  hasAdvancedPermissions: boolean
}

/**
 * Main application router that handles route access based on authentication state.
 *
 * Route Structure:
 * 1. /verify-email - Always accessible (needed during auth state transitions)
 * 2. Guest routes (/sign-in, /sign-up) - Always available to prevent 404
 *    flash when auth state changes during sign-up flow
 * 3. Landing page (/) - Only for unauthenticated users
 * 4. Authenticated routes - Protected by EmailVerificationGuard and
 *    ProfileCompletionGuard, only rendered when user is logged in
 *
 * Note: Sign-in/sign-up routes are intentionally always rendered (not conditional on !user)
 * to prevent a 404 flash that occurs during sign-up. When a user signs up:
 *   1. createUserWithEmailAndPassword() succeeds
 *   2. The auth state updates (user becomes non-null)
 *   3. The component re-renders before navigate() executes
 *   4. If guest routes were conditional, /sign-up would 404 momentarily
 *
 * The EmailVerificationGuard handles redirecting unverified users appropriately.
 */
function AppRoutes({ user, isAdmin, hasAdvancedPermissions }: AppRoutesProps) {
  return (
    <Routes>
      <Route path='/app' element={<Navigate to='/' replace />} />

      {/* Email verification - accessible regardless of auth state */}
      <Route path='/verify-email' element={<VerifyEmail />} />

      {/* Guest routes - always available to prevent 404 during auth transitions */}
      {renderRoutes(guestRoutes)}

      {/* Authenticated routes - protected by verification and profile guards */}
      {user && (
        <Route element={<EmailVerificationGuard />}>
          <Route element={<ProfileCompletionGuard user={user} />}>
            {isAdmin && renderRoutes(adminRoutes)}
            {renderRoutes(userRoutes)}
            {hasAdvancedPermissions && renderRoutes(advancedRoutes)}
            {/* Home route for authenticated users */}
            <Route path='/' element={<Home />} />
          </Route>
        </Route>
      )}

      {/* Landing page for unauthenticated users */}
      {!user && <Route path='/' element={<Landing />} />}

      <Route path='*' element={<NotFound />} />
    </Routes>
  )
}

export default AppRoutes
